/**
 * Monitored Carriers Page
 *
 * Displays user's carrier watchlist with monitoring status and quick actions.
 */

import React from 'react';
import { Truck, Plus } from 'lucide-react';
import { MonitoredCarriersList } from '../components/MonitoredCarriersList';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import '../styles/modern.css';

const MonitoredCarriersPage: React.FC = () => {
  const navigate = useNavigate();

  const handleCarrierClick = (dotNumber: number) => {
    // Navigate to carrier dashboard
    navigate(`/dashboard?dot=${dotNumber}`);
  };

  const handleRemove = (dotNumber: number) => {
    // MonitoredCarriersList handles the removal
    // This callback is for any additional actions
    logger.debug(`Carrier ${dotNumber} removed from watchlist`);
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-2xl)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-lg)' }}>
          <div>
            <h1 className="heading-1" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              <Truck style={{ width: 32, height: 32, color: 'var(--color-primary)' }} />
              Monitored Carriers
            </h1>
            <p className="text-muted" style={{ marginTop: 'var(--space-sm)', fontSize: '1rem' }}>
              Your carrier watchlist with automatic change monitoring and alerts
            </p>
          </div>

          {/* Add Carrier Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              padding: '0.75rem 1.25rem',
              fontSize: '0.9375rem',
              fontWeight: 600,
            }}
          >
            <Plus size={18} />
            Add Carrier
          </button>
        </div>

        {/* Info Card */}
        <div className="card-modern" style={{ marginBottom: 'var(--space-xl)', borderLeft: '4px solid var(--color-primary)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
            <Truck style={{ width: 24, height: 24, color: 'var(--color-primary)', flexShrink: 0 }} />
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
                Automatic Carrier Monitoring
              </h4>
              <p className="text-muted" style={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                We check your monitored carriers daily and alert you immediately when critical changes occur:
                safety ratings, operating authority, insurance status, violations, crashes, and more.
              </p>
            </div>
          </div>
        </div>

        {/* Monitored Carriers List */}
        <MonitoredCarriersList
          onCarrierClick={handleCarrierClick}
          onRemove={handleRemove}
        />
      </div>
    </div>
  );
};

export default MonitoredCarriersPage;
